import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import Achievements from '../components/Achievements';
import { TrendingUp, TrendingDown, Award, CheckCircle, AlertTriangle, Clock, Calendar, BookOpen, Medal, Target, ArrowRight, BarChart } from 'lucide-react';
import { getAllSubjects } from '../utils/subjectsAPI';
import { getUserSubjectHistory } from '../utils/subjectHistorysAPI';

/**
 * 学习报告页面组件
 * 展示用户学习数据统计、学科进度和成就
 */
function StudyReport({ session }) {
  const [subjectExamHistory, setSubjectExamHistory] = useState([]);
  const [subjectStats, setSubjectStats] = useState([]);
  const [learningDays, setLearningDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log("开始获取学习报告数据");
        
        // 获取用户ID - 优先从会话获取，然后尝试本地存储
        let userId = session?.user?.id;
        const userOpenid = localStorage.getItem('user_id');
        
        console.log("会话用户ID:", userId);
        console.log("本地存储用户ID:", userOpenid);
        
        // 如果两者都没有，使用固定值（仅用于调试）
        if (!userId && !userOpenid) {
          console.log("没有找到用户ID，使用演示模式");
          userId = 'demo-user';
        } else {
          userId = userId || userOpenid;
        }
        
        console.log("最终使用的用户ID:", userId);
        
        // 获取所有科目信息和科目考试历史记录
        const [subjectsData, subjectHistory] = await Promise.all([
          // 1. 获取所有科目信息
          getAllSubjects(),
          
          // 2. 获取科目考试历史记录 - 使用API函数替代直接数据库操作
          getUserSubjectHistory(userId)
        ]);
        
        // 处理科目数据
        if (!subjectsData) {
          console.error("获取科目信息错误");
        } else {
          console.log(`获取到${subjectsData.length}个科目信息`);
        }
        
        // 处理科目考试历史数据
        let allHistory = [];
        if (!subjectHistory || subjectHistory.length === 0) {
          console.log("没有找到科目考试历史");
        } else {
          allHistory = subjectHistory;
          console.log("获取到科目考试历史:", allHistory.length, "条记录");
          if (allHistory.length > 0) {
            console.log("科目历史记录样本数据结构:", Object.keys(allHistory[0]));
          }
          setSubjectExamHistory(allHistory);
        }
        
        // 计算学习天数
        const uniqueDates = new Set();
        allHistory.forEach(history => {
          if (history.created_at) {
            const date = new Date(history.created_at).toDateString();
            uniqueDates.add(date);
          }
        });
        setLearningDays(uniqueDates.size);
        
        // 计算科目统计数据
        if (subjectsData) {
          const subjectStatsArray = [];
          
          for (const subject of subjectsData) {
            console.log(`开始计算科目 ${subject.name} (ID: ${subject._id || subject.id}) 的统计数据`);
            
            // 寻找与该科目相关的历史记录，确保严格匹配科目ID
            const subjectHistory = allHistory.filter(h => {
              const historySubjectId = Number(h.subject_id);
              const currentSubjectId = Number(subject._id || subject.id);
              
              // 严格匹配科目ID
              const isMatch = historySubjectId === currentSubjectId;
              
              // 判断科目ID是否匹配
              if (isMatch) {
                console.log(`找到科目 ${subject.name} 的历史记录:`, h);
              }
              
              return isMatch;
            });
            
            if (subjectHistory.length > 0) {
              console.log(`找到科目 ${subject.name} 相关历史记录: ${subjectHistory.length}条`);
              
              // 计算正确率和总题数
              let totalQuestions = 0;
              let totalCorrect = 0;
              
              subjectHistory.forEach(h => {
                // 处理题目总数
                let questions = 0;
                if (h.total_questions) {
                  questions = parseInt(h.total_questions) || 0;
                } else if (h.answers && typeof h.answers === 'object') {
                  // 如果没有total_questions字段，使用answers对象的键数量
                  questions = Object.keys(h.answers).length;
                }
                
                // 处理正确题数
                let correct = 0;
                if (h.correct_count) {
                  correct = parseInt(h.correct_count) || 0;
                } else if (h.answers && h.score) {
                  // 如果有分数但没有correct_count，估算正确题数
                  const score = parseInt(h.score) || 0;
                  correct = Math.round((score / 100) * questions);
                }
                
                console.log(`科目 ${subject.name} 记录详情 - 题目数: ${questions}, 正确数: ${correct}, 分数: ${h.score}`);
                
                totalQuestions += questions;
                totalCorrect += correct;
              });
              
              const correctRate = totalQuestions > 0 
                ? Math.round((totalCorrect / totalQuestions) * 100) 
                : 0;
              
              console.log(`科目 ${subject.name} 统计结果 - 总题数: ${totalQuestions}, 正确数: ${totalCorrect}, 正确率: ${correctRate}%`);
              
              // 计算进步趋势
              let trend = 0;
              if (subjectHistory.length >= 2) {
                const sorted = [...subjectHistory].sort((a, b) => 
                  new Date(a.created_at || 0) - new Date(b.created_at || 0)
                );
                const firstScore = parseInt(sorted[0].score) || 0;
                const lastScore = parseInt(sorted[sorted.length - 1].score) || 0;
                trend = lastScore - firstScore;
              }
              
              // 获取最后学习时间
              const lastStudied = subjectHistory.reduce((latest, h) => {
                if (!h.created_at) return latest;
                const current = new Date(h.created_at);
                return !latest || current > latest ? current : latest;
              }, null);
              
              subjectStatsArray.push({
                subject,
                answeredCount: totalQuestions,
                correctCount: totalCorrect,
                correctRate,
                trend,
                lastStudied
              });
            } else {
              // 没有历史记录的科目也添加到列表中
              console.log(`科目 ${subject.name} 没有历史记录`);
              subjectStatsArray.push({
                subject,
                answeredCount: 0,
                correctCount: 0,
                correctRate: 0,
                trend: 0,
                lastStudied: null
              });
            }
          }
          
          console.log(`共计算了 ${subjectStatsArray.length} 个科目统计数据`);
          setSubjectStats(subjectStatsArray);
        }
        
      } catch (error) {
        console.error("数据加载过程中出现错误:", error.message);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [session, navigate]); // 只依赖session和navigate，避免无限循环

  // 计算总体正确率 - 按科目分组后计算总体正确率
  const calculateOverallAccuracy = () => {
    try {
      // 按科目ID分组，避免重复统计
      const subjectGroups = {};
      
      subjectExamHistory.forEach(h => {
        const subjectId = h.subject_id;
        if (!subjectId) return;
        
        if (!subjectGroups[subjectId]) {
          subjectGroups[subjectId] = [];
        }
        subjectGroups[subjectId].push(h);
      });
      
      console.log('按科目分组后:', Object.keys(subjectGroups).length, '个科目');
      
      let totalQuestions = 0;
      let totalCorrect = 0;
      
      // 对每个科目分别计算
      Object.values(subjectGroups).forEach(histories => {
        let subjectQuestions = 0;
        let subjectCorrect = 0;
        
        histories.forEach(h => {
          // 处理题目总数
          let questions = 0;
          if (h.total_questions) {
            questions = parseInt(h.total_questions) || 0;
          } else if (h.answers && typeof h.answers === 'object') {
            // 如果没有total_questions字段，使用answers对象的键数量
            questions = Object.keys(h.answers).length;
          }
          
          // 处理正确题数
          let correct = 0;
          if (h.correct_count) {
            correct = parseInt(h.correct_count) || 0;
          } else if (h.answers && h.score) {
            // 如果有分数但没有correct_count，估算正确题数
            const score = parseInt(h.score) || 0;
            correct = Math.round((score / 100) * questions);
          }
          
          subjectQuestions += questions;
          subjectCorrect += correct;
        });
        
        console.log(`科目ID ${histories[0].subject_id} - 总题数: ${subjectQuestions}, 正确数: ${subjectCorrect}`);
        totalQuestions += subjectQuestions;
        totalCorrect += subjectCorrect;
      });
      
      const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
      console.log(`总体统计: 总题数 ${totalQuestions}, 正确数 ${totalCorrect}, 正确率 ${accuracy}%`);
      
      return accuracy;
    } catch (e) {
      console.error("计算总体正确率错误:", e);
      return 0;
    }
  };
  
  // 获取平均分 - 只使用科目考试历史
  const calculateAverageScore = () => {
    try {
      if (subjectExamHistory.length === 0) return 0;
      
      const totalScore = subjectExamHistory.reduce((sum, h) => {
        return sum + (parseInt(h.score) || 0);
      }, 0);
      
      return Math.round(totalScore / subjectExamHistory.length);
    } catch (e) {
      console.error("计算平均分错误:", e);
      return 0;
    }
  };
  
  // 获取表现最好的科目
  const getBestSubject = () => {
    try {
      if (subjectStats.length === 0) return '暂无数据';
      
      const sorted = [...subjectStats].filter(s => s.answeredCount > 0)
                                     .sort((a, b) => b.correctRate - a.correctRate);
      
      return sorted.length > 0 ? sorted[0].subject.name : '暂无数据';
    } catch (e) {
      console.error("获取最佳科目错误:", e);
      return '暂无数据';
    }
  };
  
  // 获取需要加强的科目
  const getWeakestSubject = () => {
    try {
      if (subjectStats.length === 0) return '暂无数据';
      
      const sorted = [...subjectStats].filter(s => s.answeredCount > 0)
                                     .sort((a, b) => a.correctRate - b.correctRate);
      
      return sorted.length > 0 ? sorted[0].subject.name : '暂无数据';
    } catch (e) {
      console.error("获取最弱科目错误:", e);
      return '暂无数据';
    }
  };
  
  // 计算学习趋势（最近5次考试的平均分趋势）
  const calculateTrend = () => {
    try {
      if (subjectExamHistory.length < 2) return 0;
      
      // 按时间排序
      const sorted = [...subjectExamHistory].sort((a, b) => {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      
      const recent = sorted.slice(0, Math.min(5, sorted.length));
      const newest = parseInt(recent[0].score) || 0;
      const oldest = parseInt(recent[recent.length - 1].score) || 0;
      
      return newest - oldest;
    } catch (e) {
      console.error("计算学习趋势错误:", e);
      return 0;
    }
  };
  
  // 获取累计答题数 - 按科目分组后计算
  const getTotalQuestions = () => {
    try {
      // 按科目ID分组，避免重复统计
      const subjectGroups = {};
      
      subjectExamHistory.forEach(h => {
        const subjectId = h.subject_id;
        if (!subjectId) return;
        
        if (!subjectGroups[subjectId]) {
          subjectGroups[subjectId] = [];
        }
        subjectGroups[subjectId].push(h);
      });
      
      let totalQuestions = 0;
      
      // 对每个科目分别计算
      Object.values(subjectGroups).forEach(histories => {
        let subjectQuestions = 0;
        
        histories.forEach(h => {
          // 确保total_questions字段存在
          if (h.total_questions) {
            subjectQuestions += parseInt(h.total_questions) || 0;
          } 
          // 如果total_questions不存在但answers存在，计算答案数量
          else if (h.answers && typeof h.answers === 'object') {
            subjectQuestions += Object.keys(h.answers).length;
          }
        });
        
        console.log(`科目ID ${histories[0].subject_id} 的答题数: ${subjectQuestions}`);
        totalQuestions += subjectQuestions;
      });
      
      console.log('计算累计答题数:', totalQuestions);
      return totalQuestions;
    } catch (e) {
      console.error("计算累计答题数错误:", e);
      return 0;
    }
  };
  
  // 时间转换
  const formatDate = (dateString) => {
    if (!dateString) return '暂无记录';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return '日期错误';
    }
  };

  // 获取最后学习时间
  const getLastStudyDate = () => {
    try {
      if (subjectExamHistory.length === 0) return '暂无记录';
      
      // 找出最新的学习记录
      const latest = subjectExamHistory.reduce((latestDate, h) => {
        if (!h.created_at) return latestDate;
        const current = new Date(h.created_at);
        return !latestDate || current > latestDate ? current : latestDate;
      }, null);
      
      return latest ? formatDate(latest) : '暂无记录';
    } catch (e) {
      console.error("获取最后学习日期错误:", e);
      return '暂无记录';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <Header title="学习报告" showBack={false} />
      
      <div className="p-4">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 mb-4 rounded-lg text-sm">
            数据加载错误: {error}
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md p-5 mb-6">
          <h3 className="font-bold text-lg text-gray-800 mb-4">学习概览</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl">
              <div className="text-blue-800 text-sm flex items-center">
                <Calendar className="w-3 h-3 mr-1" />
                总学习天数
              </div>
              <div className="text-2xl font-bold text-blue-600 mt-1">{learningDays} 天</div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-xl">
              <div className="text-green-800 text-sm flex items-center">
                <Target className="w-3 h-3 mr-1" />
                累计答题数
              </div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {getTotalQuestions()}
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-xl">
              <div className="text-purple-800 text-sm flex items-center">
                <Medal className="w-3 h-3 mr-1" />
                平均分
              </div>
              <div className="text-2xl font-bold text-purple-600 mt-1">
                {calculateAverageScore()}
              </div>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-xl">
              <div className="text-orange-800 text-sm flex items-center">
                <BarChart className="w-3 h-3 mr-1" />
                总体正确率
              </div>
              <div className="text-2xl font-bold text-orange-600 mt-1">
                {calculateOverallAccuracy()}%
              </div>
            </div>
          </div>
          
          <div className="mt-6 space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">学习趋势</span>
              <span className={`font-medium ${calculateTrend() >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {calculateTrend() >= 0 ? (
                  <span className="flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    上升 {calculateTrend()} 分
                  </span>
                ) : (
                  <span className="flex items-center">
                    <TrendingDown className="w-4 h-4 mr-1" />
                    下降 {Math.abs(calculateTrend())} 分
                  </span>
                )}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">最佳学科</span>
              <span className="font-medium text-green-500 flex items-center">
                <Award className="w-4 h-4 mr-1" />
                {getBestSubject()}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">需加强学科</span>
              <span className="font-medium text-orange-500 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {getWeakestSubject()}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">上次学习</span>
              <span className="font-medium text-gray-700 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {getLastStudyDate()}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-5 mb-6">
          <h3 className="font-bold text-lg text-gray-800 mb-4">学科进度</h3>
          
          {subjectStats.filter(s => s.answeredCount > 0).length > 0 ? (
            <div className="space-y-6">
              {subjectStats.filter(s => s.answeredCount > 0).map((stats, index) => (
                <div key={index} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-800 flex items-center">
                      <BookOpen className="w-4 h-4 mr-1 text-primary" />
                      {stats.subject.name}
                    </h4>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 mr-2 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {stats.lastStudied ? formatDate(stats.lastStudied) : '未学习'}
                      </span>
                      <span className={`flex items-center text-xs font-medium ${
                        stats.trend > 0 ? 'text-green-500' : stats.trend < 0 ? 'text-red-500' : 'text-gray-500'
                      }`}>
                        {stats.trend > 0 ? (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        ) : stats.trend < 0 ? (
                          <TrendingDown className="w-3 h-3 mr-1" />
                        ) : (
                          <span>-</span>
                        )}
                        {stats.trend !== 0 && `${stats.trend > 0 ? '+' : ''}${stats.trend}`}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center mb-1">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full ${
                          stats.correctRate >= 80 ? 'bg-green-500' : 
                          stats.correctRate >= 60 ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${stats.correctRate}%` }}
                      ></div>
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-700">{stats.correctRate}%</span>
                  </div>
                  
                  <div className="text-xs text-gray-500 flex justify-between">
                    <span>已答题: {stats.answeredCount}</span>
                    <span className="flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                      正确数: {stats.correctCount}
                    </span>
                  </div>
                </div>
              ))}
              
              {subjectStats.filter(s => s.answeredCount === 0).length > 0 && (
                <div className="border-t border-gray-100 pt-4 mt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-sm font-medium text-gray-700">未学习科目</h5>
                    <span className="text-xs text-gray-500">{subjectStats.filter(s => s.answeredCount === 0).length}个</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {subjectStats.filter(s => s.answeredCount === 0).map((stats, index) => (
                      <div 
                        key={`unlearned-${index}`} 
                        className="flex items-center bg-gray-50 p-2 rounded text-xs text-gray-600"
                      >
                        <BookOpen className="w-3 h-3 mr-1 text-gray-400" />
                        {stats.subject.name}
                        <ArrowRight className="w-3 h-3 ml-auto text-gray-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>暂无学习数据</p>
            </div>
          )}
        </div>
        
        {/* 成就系统 - 直接使用组件，无需额外容器 */}
        <Achievements examHistory={subjectExamHistory} />
      </div>
      
      <TabBar />
    </div>
  );
}

export default StudyReport; 